// ============================================================
// Die Reiterleiste am unteren Rand — nur auf dem Telefon (Muster lilasprojekte,
// SPEC §13.2): 62 px hoch, 14 px vom Rand, Glas. Reiter nach dem Canvas (Paket D):
// Kataloge · Bibliothek · Modell · Konto. „Modell" erscheint nur, wo ein Projekt
// feststeht (`/p/:projektId/…`); der Adminbereich liegt am Telefon unter „Konto".
//
// Die Komponente entscheidet SELBST, ob sie erscheint (`useMobil()`), damit ein
// Bildschirm seine Bodenblatt-Höhe aus derselben Wahrheit rechnen kann.
// ============================================================
import { Link, matchPath, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { schluessel } from "../../api/schluessel";
import type { Katalog } from "../../api/types";
import { Icon } from "../ui";
import type { Reiter } from "./TopNav";
import { useMobil } from "../../hooks/useMedienabfrage";
import { useT } from "../../lib/texte";
import { reiterAn, weg } from "../../lib/wege";

/** Die Reiter der Modellseite eines Projekts (`weg.modell`, `weg.zuordnen`, `weg.importe`, `weg.bericht`). */
export function imModell(pfad: string): boolean {
  return matchPath("/p/:projektId/modell/*", pfad) !== null || matchPath("/p/:projektId/zuordnen/*", pfad) !== null || matchPath("/p/:projektId/importe/*", pfad) !== null;
}

export function MobilLeiste() {
  const mobil = useMobil();
  const { t } = useT();
  const { pathname } = useLocation();
  // Im Katalog (`/k/:katalogId/…`) steht das Projekt nicht in der Adresse, wohl aber im
  // geladenen Katalog: nur aus dem Zwischenspeicher lesen (`enabled: false`), nie selbst
  // holen. Vorher fehlte „Modell" im Editor (Prüfung Runde 1).
  const katalogId = matchPath("/k/:katalogId/*", pathname)?.params.katalogId;
  const { data: katalog } = useQuery<Katalog>({ queryKey: schluessel.katalog(katalogId ?? ""), enabled: false });
  if (!mobil) return null;

  const projektId = matchPath("/p/:projektId/*", pathname)?.params.projektId ?? (katalogId ? katalog?.projekt.id : undefined);
  const modell = projektId ? weg.modell(projektId) : null;
  const punkte: Reiter[] = [
    { zu: weg.kataloge(), text: t.nav.kataloge, icon: "book-open", genau: true },
    { zu: weg.bibliothek(), text: t.nav.bibliothek, icon: "books" },
    ...(modell ? [{ zu: modell, text: t.nav.modell, icon: "cube" }] : []),
    { zu: weg.profil(), text: t.nav.konto, icon: "user-circle" },
  ];

  return (
    <nav className="mobil-leiste glass-strong" aria-label={t.nav.bereiche}>
      {punkte.map((p) => {
        // Im Projekt leuchtet „Modell", nicht zusätzlich „Kataloge" (reiterAn zählt /p/ zu „/").
        // „Modell" gilt für alle drei Reiter der Modellseite — Modell, Zuordnen, Importe samt
        // Bericht — wie im Canvas; vorher war dort kein Reiter aktiv (Abnahme Runde 2).
        const an =
          p.zu === "/" && modell
            ? false
            : p.zu === modell
              ? imModell(pathname)
              : reiterAn(p.zu, pathname, p.genau) || (p.zu === weg.profil() && pathname.startsWith("/admin"));
        return (
          <Link key={p.zu} to={p.zu} aria-current={an ? "page" : undefined} className={`ring-focus${an ? " aktiv" : ""}`}>
            <Icon name={p.icon} size={21} />
            {p.text}
          </Link>
        );
      })}
    </nav>
  );
}
